import { format } from 'date-fns';
import i18n from '../i18n';
import { PRODUCTION_EVENT_TYPES, EVENT_TYPE_GROUPS, taskDisplayName } from './productionEvents';
import { formatDuration, formatCurrency } from './labour';
import { formatTime, formatDateTime } from './time';
import { getBenchmarkKey } from './benchmark';
import { NOTE_CATEGORY_MAP } from '../hooks/useTaskNotes';
import { calcActiveElapsedMinutes } from './replayLive';

const NO_ROOM_KEY = '__no_room__';
const UNASSIGNED_KEY = '__unassigned__';

const LIFECYCLE_EVENT_TYPES = new Set([
  PRODUCTION_EVENT_TYPES.TASK_STARTED,
  PRODUCTION_EVENT_TYPES.TASK_PAUSED,
  PRODUCTION_EVENT_TYPES.TASK_RESUMED,
  PRODUCTION_EVENT_TYPES.TASK_COMPLETED,
  PRODUCTION_EVENT_TYPES.TASK_CANCELLED,
]);

/** When timestamps tie, show lifecycle events in logical order. */
const EVENT_SORT_ORDER = {
  [PRODUCTION_EVENT_TYPES.TASK_STARTED]: 1,
  [PRODUCTION_EVENT_TYPES.WORKER_ADDED]: 2,
  [PRODUCTION_EVENT_TYPES.TASK_PAUSED]: 3,
  [PRODUCTION_EVENT_TYPES.MATERIAL_DELAY_LOGGED]: 4,
  [PRODUCTION_EVENT_TYPES.NOTE_ADDED]: 5,
  [PRODUCTION_EVENT_TYPES.TASK_RESUMED]: 6,
  [PRODUCTION_EVENT_TYPES.HANDOFF]: 7,
  [PRODUCTION_EVENT_TYPES.ROOM_CHANGED]: 8,
  [PRODUCTION_EVENT_TYPES.QUANTITY_UPDATED]: 9,
  [PRODUCTION_EVENT_TYPES.TASK_COMPLETED]: 10,
  [PRODUCTION_EVENT_TYPES.TASK_CANCELLED]: 11,
  [PRODUCTION_EVENT_TYPES.TASK_EDITED]: 12,
};

function percentile(values, p) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.ceil((p / 100) * sorted.length) - 1;
  return sorted[Math.max(0, idx)];
}

function parseFlexibleDate(raw) {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date) {
    return isNaN(raw.getTime()) ? null : raw;
  }
  if (typeof raw === 'object') {
    if (typeof raw.toDate === 'function') {
      const d = raw.toDate();
      return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    }
    const seconds = raw.seconds ?? raw._seconds;
    if (typeof seconds === 'number') {
      const nanos = raw.nanoseconds ?? raw._nanoseconds ?? 0;
      const d = new Date(seconds * 1000 + nanos / 1e6);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }
  if (typeof raw === 'number') {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof raw === 'string') {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function toDate(raw) {
  return parseFlexibleDate(raw);
}

function eventKey(taskId, eventType, ts) {
  const t = ts?.getTime?.() ?? 0;
  return `${taskId}:${eventType}:${Math.floor(t / 60000)}`;
}

function normalizeEvent(doc, source = 'logged') {
  const ts = toDate(doc.timestamp ?? doc.createdAt);
  return {
    id: doc.id || eventKey(doc.taskId, doc.eventType, ts),
    taskId: doc.taskId,
    eventType: doc.eventType,
    timestamp: ts,
    userId: doc.userId || doc.createdBy || null,
    taskName: doc.taskName || null,
    taskType: doc.taskType || null,
    taskCategoryId: doc.taskCategoryId || null,
    taskCategoryName: doc.taskCategoryName || null,
    productName: doc.productName || null,
    roomId: doc.roomId || null,
    roomName: doc.roomName || null,
    employeeNames: doc.employeeNames || [],
    employeeIds: doc.employeeIds || [],
    teamSize: doc.teamSize ?? null,
    notes: doc.notes || null,
    reason: doc.reason || null,
    previousValue: doc.previousValue ?? null,
    newValue: doc.newValue ?? null,
    durationMinutes: doc.durationMinutes ?? null,
    metadata: doc.metadata || null,
    source,
    synthetic: source !== 'logged',
  };
}

/** Reconstruct coarse events from task records when no granular log exists. */
export function synthesizeEventsFromTask(task, dayStart, dayEnd) {
  const events = [];
  const name = taskDisplayName(task);
  const base = {
    taskId: task.id,
    taskName: name,
    taskType: task.taskType,
    taskCategoryName: task.taskCategoryName,
    productName: task.productName,
    roomId: task.roomId,
    roomName: task.roomName,
    employeeNames: task.employeeNames || [],
    employeeIds: task.employeeIds || [],
    teamSize: task.teamSize,
    userId: task.createdBy,
  };

  const start = toDate(task.startTime ?? task.createdAt);
  if (start && start >= dayStart && start <= dayEnd) {
    events.push(normalizeEvent({
      ...base,
      eventType: PRODUCTION_EVENT_TYPES.TASK_STARTED,
      timestamp: start,
    }, 'synthesized'));
  }

  const end = toDate(task.submittedAt ?? task.endTime);
  if (task.status === 'completed' && end && end >= dayStart && end <= dayEnd) {
    events.push(normalizeEvent({
      ...base,
      eventType: PRODUCTION_EVENT_TYPES.TASK_COMPLETED,
      timestamp: end,
      durationMinutes: task.netDurationMinutes ?? task.durationMinutes,
      metadata: {
        totalDurationMinutes: task.durationMinutes,
        downtimeMinutes: task.downtimeMinutes,
        quantityCompleted: task.quantityCompleted,
        labourCost: task.labourCost,
      },
      notes: task.notes,
      reason: task.downtimeReason,
    }, 'synthesized'));

    if (task.downtimeMinutes > 0) {
      events.push(normalizeEvent({
        ...base,
        eventType: PRODUCTION_EVENT_TYPES.TASK_PAUSED,
        timestamp: end,
        durationMinutes: task.downtimeMinutes,
        notes: task.downtimeReason,
        metadata: { syntheticPauseSummary: true },
      }, 'synthesized'));
    }
  }

  if (task.status === 'abandoned') {
    const cancelledAt = toDate(task.editedAt ?? task.updatedAt);
    if (cancelledAt && cancelledAt >= dayStart && cancelledAt <= dayEnd) {
      events.push(normalizeEvent({
        ...base,
        eventType: PRODUCTION_EVENT_TYPES.TASK_CANCELLED,
        timestamp: cancelledAt,
        reason: task.editReason,
      }, 'synthesized'));
    }
  }

  // Active task still running on the selected day
  if (task.status === 'active' && start && start <= dayEnd) {
    if (task.pausedAt) {
      const pausedAt = toDate(task.pausedAt);
      if (pausedAt && pausedAt >= dayStart && pausedAt <= dayEnd) {
        events.push(normalizeEvent({
          ...base,
          eventType: PRODUCTION_EVENT_TYPES.TASK_PAUSED,
          timestamp: pausedAt,
        }, 'live'));
      }
    }
  }

  return events;
}

export function synthesizeEventsFromNote(note, taskMap) {
  const ts = toDate(note.createdAt);
  const task = taskMap.get(note.taskId);
  const isMaterialDelay = note.category === 'material-delay';
  return normalizeEvent({
    id: `note-${note.id}`,
    taskId: note.taskId,
    eventType: isMaterialDelay
      ? PRODUCTION_EVENT_TYPES.MATERIAL_DELAY_LOGGED
      : PRODUCTION_EVENT_TYPES.NOTE_ADDED,
    timestamp: ts,
    userId: note.userId,
    taskName: task ? taskDisplayName(task) : null,
    taskType: task?.taskType,
    taskCategoryName: task?.taskCategoryName,
    productName: task?.productName,
    roomName: task?.roomName,
    employeeNames: task?.employeeNames || [],
    notes: note.noteText,
    reason: NOTE_CATEGORY_MAP[note.category]?.label || note.category,
    metadata: { noteCategory: note.category, severity: note.severity },
  }, 'note');
}

export function synthesizeEventsFromActivityLog(log, taskMap) {
  if (log.action !== 'edit_task') return null;
  const ts = toDate(log.createdAt);
  const task = taskMap.get(log.entityId);
  const normalizedBefore = normalizeTaskEditSnapshot(log.before);
  const normalizedAfter = normalizeTaskEditSnapshot(log.after);
  const changed = getTaskEditChangedFields(normalizedBefore, normalizedAfter);
  return normalizeEvent({
    id: `edit-${log.id}`,
    taskId: log.entityId,
    eventType: changed.includes('quantityCompleted')
      ? PRODUCTION_EVENT_TYPES.QUANTITY_UPDATED
      : PRODUCTION_EVENT_TYPES.TASK_EDITED,
    timestamp: ts,
    userId: log.userId,
    taskName: task ? taskDisplayName(task) : log.after?.taskName,
    productName: log.after?.productName,
    reason: log.reason,
    previousValue: changed.length
      ? JSON.stringify(Object.fromEntries(changed.map((key) => [key, normalizedBefore[key]])))
      : null,
    newValue: changed.length
      ? JSON.stringify(Object.fromEntries(changed.map((key) => [key, normalizedAfter[key]])))
      : null,
    metadata: { changedFields: changed },
  }, 'audit');
}

/**
 * Merge logged events with synthesized history, dedupe overlapping sources.
 */
export function mergeReplayEvents({ loggedEvents, tasks, notes, activityLogs, dayStart, dayEnd }) {
  const taskMap = new Map(tasks.map((t) => [t.id, t]));
  const seen = new Set();
  const merged = [];

  const loggedNoteIds = new Set(
    loggedEvents
      .map((d) => d.metadata?.noteId)
      .filter(Boolean),
  );
  const loggedEditIds = new Set(
    loggedEvents
      .map((d) => d.metadata?.activityLogId)
      .filter(Boolean),
  );
  const loggedLifecycle = new Set(
    loggedEvents
      .filter((d) => LIFECYCLE_EVENT_TYPES.has(d.eventType))
      .map((d) => `${d.taskId}:${d.eventType}`),
  );

  for (const doc of loggedEvents) {
    const ev = normalizeEvent(doc, 'logged');
    if (!ev.timestamp) continue;
    const key = eventKey(ev.taskId, ev.eventType, ev.timestamp);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(ev);
  }

  for (const task of tasks) {
    for (const ev of synthesizeEventsFromTask(task, dayStart, dayEnd)) {
      if (loggedLifecycle.has(`${ev.taskId}:${ev.eventType}`)) continue;

      const key = eventKey(ev.taskId, ev.eventType, ev.timestamp);
      if (seen.has(key)) continue;
      if (ev.metadata?.syntheticPauseSummary) {
        const hasRealPauses = merged.some(
          (e) => e.taskId === ev.taskId
            && e.eventType === PRODUCTION_EVENT_TYPES.TASK_PAUSED
            && !e.metadata?.syntheticPauseSummary,
        );
        if (hasRealPauses) continue;
      }
      seen.add(key);
      merged.push(ev);
    }
  }

  for (const note of notes) {
    if (loggedNoteIds.has(note.id)) continue;
    if (hasLoggedNoteNear(loggedEvents, note)) continue;

    const ev = synthesizeEventsFromNote(note, taskMap);
    if (!ev.timestamp || ev.timestamp < dayStart || ev.timestamp > dayEnd) continue;
    const key = `note:${note.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(ev);
  }

  for (const log of activityLogs) {
    if (loggedEditIds.has(log.id)) continue;
    if (hasLoggedEditNear(loggedEvents, log)) continue;

    const ev = synthesizeEventsFromActivityLog(log, taskMap);
    if (!ev || !ev.timestamp || ev.timestamp < dayStart || ev.timestamp > dayEnd) continue;
    const key = `edit:${log.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(ev);
  }

  merged.sort(compareEvents);
  return merged;
}

function hasLoggedNoteNear(loggedEvents, note) {
  const ts = toDate(note.createdAt);
  if (!ts) return false;
  return loggedEvents.some((e) => {
    if (e.taskId !== note.taskId) return false;
    if (e.eventType !== PRODUCTION_EVENT_TYPES.NOTE_ADDED
      && e.eventType !== PRODUCTION_EVENT_TYPES.MATERIAL_DELAY_LOGGED) return false;
    const eTs = toDate(e.timestamp);
    return eTs && Math.abs(eTs.getTime() - ts.getTime()) < 120_000;
  });
}

function hasLoggedEditNear(loggedEvents, log) {
  const ts = toDate(log.createdAt);
  if (!ts) return false;
  return loggedEvents.some((e) => {
    if (e.taskId !== log.entityId) return false;
    if (e.eventType !== PRODUCTION_EVENT_TYPES.TASK_EDITED
      && e.eventType !== PRODUCTION_EVENT_TYPES.QUANTITY_UPDATED) return false;
    const eTs = toDate(e.timestamp);
    return eTs && Math.abs(eTs.getTime() - ts.getTime()) < 120_000;
  });
}

function compareEvents(a, b) {
  const diff = a.timestamp - b.timestamp;
  if (diff !== 0) return diff;
  return (EVENT_SORT_ORDER[a.eventType] ?? 99) - (EVENT_SORT_ORDER[b.eventType] ?? 99);
}

export const REPLAY_EVENT_TYPE_OPTIONS = [
  { value: '', labelKey: 'replay.filters.allEvents' },
  { value: 'active', labelKey: 'replay.groups.active' },
  { value: 'pause', labelKey: 'replay.groups.pause' },
  { value: 'delay', labelKey: 'replay.groups.delay' },
  { value: 'handoff', labelKey: 'replay.groups.handoff' },
  { value: 'completion', labelKey: 'replay.groups.completion' },
  { value: 'edit', labelKey: 'replay.groups.edit' },
  { value: 'note', labelKey: 'replay.groups.note' },
];

export function filterReplayEvents(events, filters, tasksById) {
  let result = events;

  const highCostThreshold = filters.highCostThreshold ?? null;

  if (filters.eventGroup) {
    const group = EVENT_TYPE_GROUPS[filters.eventGroup];
    if (group) result = result.filter((e) => group.has(e.eventType));
  }

  if (filters.eventType) {
    result = result.filter((e) => e.eventType === filters.eventType);
  }

  if (filters.workerName) {
    const w = filters.workerName.toLowerCase();
    result = result.filter((e) => {
      if (e.employeeNames?.some((n) => n.toLowerCase().includes(w))) return true;
      const task = tasksById.get(e.taskId);
      return task?.employeeNames?.some((n) => n.toLowerCase().includes(w));
    });
  }

  if (filters.roomId) {
    result = result.filter((e) => {
      if (e.roomId === filters.roomId) return true;
      const task = tasksById.get(e.taskId);
      return task?.roomId === filters.roomId;
    });
  }

  if (filters.taskCategoryId) {
    result = result.filter((e) => {
      const task = tasksById.get(e.taskId);
      return (e.taskCategoryName && task?.taskCategoryId === filters.taskCategoryId)
        || task?.taskCategoryId === filters.taskCategoryId;
    });
  }

  if (filters.tagId) {
    result = result.filter((e) => {
      const task = tasksById.get(e.taskId);
      if (task?.tagIds?.includes(filters.tagId)) return true;
      return e.tagIds?.includes(filters.tagId);
    });
  }

  if (filters.productName) {
    const p = filters.productName.toLowerCase();
    result = result.filter((e) =>
      e.productName?.toLowerCase().includes(p)
      || tasksById.get(e.taskId)?.productName?.toLowerCase().includes(p),
    );
  }

  if (filters.taskType && filters.taskType !== 'all') {
    result = result.filter((e) =>
      e.taskType === filters.taskType
      || tasksById.get(e.taskId)?.taskType === filters.taskType,
    );
  }

  if (filters.search) {
    const s = filters.search.toLowerCase();
    result = result.filter((e) =>
      e.taskName?.toLowerCase().includes(s)
      || e.productName?.toLowerCase().includes(s)
      || e.roomName?.toLowerCase().includes(s)
      || e.notes?.toLowerCase().includes(s)
      || e.employeeNames?.some((n) => n.toLowerCase().includes(s)),
    );
  }

  if (filters.highCost && highCostThreshold != null) {
    result = result.filter((e) => {
      const task = tasksById.get(e.taskId);
      return task?.labourCost != null && task.labourCost >= highCostThreshold;
    });
  } else if (filters.highCost) {
    result = result.filter((e) => {
      const task = tasksById.get(e.taskId);
      return task?.labourCost != null && task.labourCost > 0;
    });
  }

  if (filters.delayed) {
    result = result.filter((e) =>
      e.eventType === PRODUCTION_EVENT_TYPES.MATERIAL_DELAY_LOGGED
      || (e.eventType === PRODUCTION_EVENT_TYPES.TASK_PAUSED && (e.durationMinutes ?? 0) >= 15)
      || (tasksById.get(e.taskId)?.downtimeMinutes ?? 0) > 0,
    );
  }

  if (filters.incomplete) {
    result = result.filter((e) => {
      const task = tasksById.get(e.taskId);
      return task?.status === 'active' || task?.status === 'abandoned';
    });
  }

  if (filters.statusScope === 'completed') {
    result = result.filter((e) => {
      const task = tasksById.get(e.taskId);
      return !task || task.status === 'completed';
    });
  } else if (filters.statusScope === 'active') {
    result = result.filter((e) => tasksById.get(e.taskId)?.status === 'active');
  }

  if (filters.needsAttention && filters.needsAttentionTaskIds?.size) {
    result = result.filter((e) => filters.needsAttentionTaskIds.has(e.taskId));
  }

  if (filters.notesOnly) {
    result = result.filter((e) =>
      e.eventType === PRODUCTION_EVENT_TYPES.NOTE_ADDED
      || e.eventType === PRODUCTION_EVENT_TYPES.MATERIAL_DELAY_LOGGED
      || Boolean(e.notes),
    );
  }

  return result;
}

function workerLabel(ev) {
  if (ev.employeeNames?.length) return ev.employeeNames.join(', ');
  if (ev.teamSize > 1) return `Team × ${ev.teamSize}`;
  return null;
}

function taskStartedInDay(task, dayStart, dayEnd) {
  const start = toDate(task.startTime ?? task.createdAt);
  return start != null && start >= dayStart && start <= dayEnd;
}

function taskCompletedInDay(task, dayStart, dayEnd) {
  if (task.status !== 'completed') return false;
  const end = toDate(task.submittedAt ?? task.endTime);
  return end != null && end >= dayStart && end <= dayEnd;
}

export function computeReplaySummary(
  events,
  tasks,
  dayStart,
  dayEnd,
  currency = 'CAD',
  { asOf = new Date(), includeActiveElapsed = false } = {},
) {
  const taskMap = new Map(tasks.map((t) => [t.id, t]));
  let pauseMinutes = 0;
  let labourCost = 0;
  let units = 0;
  let delayCount = 0;
  let longestTask = null;
  let biggestDelay = null;
  const workerMinutes = {};
  const roomMinutes = {};

  const tasksStarted = tasks.filter((t) => taskStartedInDay(t, dayStart, dayEnd)).length;
  const tasksCompleted = tasks.filter((t) => taskCompletedInDay(t, dayStart, dayEnd)).length;

  // Exclude synthetic pause summaries from pause counts in narrative
  const pauseEvents = events.filter(
    (e) => e.eventType === PRODUCTION_EVENT_TYPES.TASK_PAUSED
      && !e.metadata?.syntheticPauseSummary,
  );
  const pauseCount = pauseEvents.length;

  for (const ev of events) {
    if (ev.eventType === PRODUCTION_EVENT_TYPES.MATERIAL_DELAY_LOGGED) delayCount += 1;

    const task = taskMap.get(ev.taskId);

    if (ev.eventType === PRODUCTION_EVENT_TYPES.TASK_COMPLETED) {
      const net = ev.durationMinutes ?? task?.netDurationMinutes ?? 0;
      const w = workerLabel(ev) || UNASSIGNED_KEY;
      workerMinutes[w] = (workerMinutes[w] || 0) + net;
      const room = ev.roomName || task?.roomName || NO_ROOM_KEY;
      roomMinutes[room] = (roomMinutes[room] || 0) + net;
    }
  }

  for (const ev of pauseEvents) {
    const dm = ev.durationMinutes ?? 0;
    if (dm > 0 && (!biggestDelay || dm > biggestDelay.minutes)) {
      biggestDelay = { name: ev.taskName, minutes: dm, reason: ev.notes || ev.reason };
    }
  }

  for (const t of tasks) {
    const dm = t.downtimeMinutes ?? 0;
    if (dm > 0 && (!biggestDelay || dm > biggestDelay.minutes)) {
      biggestDelay = { name: taskDisplayName(t), minutes: dm, reason: t.downtimeReason };
    }
  }

  // Deduplicate cost/units — count each task completed on this day once
  labourCost = 0;
  units = 0;
  for (const t of tasks) {
    if (!taskCompletedInDay(t, dayStart, dayEnd)) continue;
    if (t.labourCost != null) labourCost += t.labourCost;
    if (t.quantityCompleted != null) units += t.quantityCompleted;
    const net = t.netDurationMinutes ?? t.durationMinutes ?? 0;
    if (!longestTask || net > longestTask.minutes) {
      longestTask = { name: taskDisplayName(t), minutes: net };
    }
  }

  let activeMinutes = tasks
    .filter((t) => taskCompletedInDay(t, dayStart, dayEnd))
    .reduce((sum, t) => sum + (t.netDurationMinutes ?? t.durationMinutes ?? 0), 0);

  if (includeActiveElapsed) {
    const summaryAsOf = asOf > dayEnd ? dayEnd : asOf;
    for (const t of tasks) {
      if (t.status !== 'active') continue;
      const elapsed = calcActiveElapsedMinutes(t, summaryAsOf);
      if (elapsed != null && elapsed > 0) activeMinutes += elapsed;
    }
  }

  pauseMinutes = tasks.reduce((sum, t) => sum + (t.downtimeMinutes ?? 0), 0);

  const mostActiveWorker = Object.entries(workerMinutes).sort((a, b) => b[1] - a[1])[0];
  const mostActiveRoom = Object.entries(roomMinutes).sort((a, b) => b[1] - a[1])[0];

  const incompleteActive = tasks.filter((t) => t.status === 'active').length;

  const startEvents = events.filter((e) => e.eventType === PRODUCTION_EVENT_TYPES.TASK_STARTED);
  const firstStart = startEvents.length
    ? startEvents.reduce((earliest, e) => (!earliest || e.timestamp < earliest.timestamp ? e : earliest))
    : null;
  const startTimeStr = firstStart ? formatTime(firstStart.timestamp) : null;

  const avgCostPerUnit = units > 0 && labourCost > 0 ? labourCost / units : null;

  const hasActivity = tasksStarted > 0 || tasksCompleted > 0 || incompleteActive > 0;

  const narrative = buildNarrative({
    startTimeStr,
    pauseCount,
    delayCount,
    incompleteActive,
    hasActivity,
  });

  return {
    tasksStarted,
    tasksCompleted,
    activeMinutes,
    pauseMinutes,
    labourCost,
    units,
    avgCostPerUnit,
    longestTask,
    biggestDelay,
    mostActiveWorker: mostActiveWorker ? {
      name: mostActiveWorker[0] === UNASSIGNED_KEY
        ? i18n.t('replayExtra.unassigned')
        : mostActiveWorker[0],
      minutes: mostActiveWorker[1],
    } : null,
    mostActiveRoom: mostActiveRoom && mostActiveRoom[0] !== NO_ROOM_KEY
      ? { name: mostActiveRoom[0], minutes: mostActiveRoom[1] }
      : null,
    incompleteActive,
    narrative,
    currency,
  };
}

function buildNarrative({
  startTimeStr, pauseCount, delayCount, incompleteActive, hasActivity,
}) {
  if (!hasActivity) {
    return i18n.t('replayExtra.narrative.empty');
  }

  const headline = startTimeStr
    ? i18n.t('replayExtra.narrative.startedAt', { time: startTimeStr })
    : i18n.t('replayExtra.narrative.default');

  if (delayCount > 0) {
    return `${headline} ${i18n.t('replayExtra.narrative.delays', { count: delayCount })}`;
  }

  if (pauseCount > 0) {
    return `${headline} ${i18n.t('replayExtra.narrative.pauses', { count: pauseCount })}`;
  }

  return headline;
}

/** Active tasks with elapsed time for attention callouts. */
export function getActiveTaskSummaries(tasks, asOf = new Date()) {
  return tasks
    .filter((t) => t.status === 'active')
    .map((t) => {
      const start = toDate(t.startTime ?? t.createdAt);
      const elapsedMinutes = start
        ? Math.max(0, Math.round((asOf.getTime() - start.getTime()) / 60000))
        : null;
      return {
        id: t.id,
        name: taskDisplayName(t),
        worker: t.employeeNames?.length ? t.employeeNames.join(', ') : null,
        room: t.roomName || null,
        category: t.taskCategoryName || null,
        elapsedMinutes,
        paused: Boolean(t.pausedAt),
      };
    });
}

/** Plain-language operational insights not covered elsewhere on the page. */
export function buildReplayInsights({ gaps, events, activeTasks }) {
  const insights = [];
  const hasActiveTasks = activeTasks.length > 0;

  const longRuns = events.filter((e) => e.highlight === 'long-running');
  if (longRuns.length === 1) {
    insights.push({
      id: 'long-run-one',
      message: i18n.t('replayExtra.insights.longRunOne', {
        name: longRuns[0].taskName || i18n.t('replayExtra.insights.oneTask'),
      }),
    });
  } else if (longRuns.length > 1) {
    insights.push({
      id: 'long-run-many',
      message: i18n.t('replayExtra.insights.longRunMany', { count: longRuns.length }),
    });
  }

  const longestGap = gaps.length
    ? gaps.reduce((max, g) => (g.minutes > max.minutes ? g : max), gaps[0])
    : null;
  if (longestGap && longestGap.minutes >= 60) {
    const from = formatTime(longestGap.start);
    const to = formatTime(longestGap.end);
    insights.push({
      id: 'inactivity-gap',
      message: i18n.t('replayExtra.insights.inactivityGap', {
        minutes: longestGap.minutes,
        from,
        to,
      }),
    });
  } else if (longestGap && longestGap.minutes >= 30 && !hasActiveTasks) {
    insights.push({
      id: 'inactivity-gap-short',
      message: i18n.t('replayExtra.insights.inactivityGapShort', { minutes: longestGap.minutes }),
    });
  }

  const longPauses = events.filter(
    (e) => e.eventType === PRODUCTION_EVENT_TYPES.TASK_PAUSED
      && (e.durationMinutes ?? 0) >= 45
      && !e.metadata?.syntheticPauseSummary,
  );
  if (longPauses.length === 1 && !longRuns.length) {
    insights.push({
      id: 'long-pause',
      message: i18n.t('replayExtra.insights.longPause', {
        duration: formatDuration(longPauses[0].durationMinutes),
        name: longPauses[0].taskName || i18n.t('replayExtra.insights.aTask'),
      }),
    });
  }

  return insights.slice(0, 4);
}

export function getEventCostAndUnits(event, task) {
  const units = event.metadata?.quantityCompleted ?? task?.quantityCompleted ?? null;
  const cost = event.metadata?.labourCost ?? task?.labourCost ?? null;
  return { units, cost };
}

/** Detect gaps with no production activity (> 30 min between events). */
export function detectActivityGaps(events, thresholdMinutes = 30) {
  const gaps = [];
  for (let i = 1; i < events.length; i += 1) {
    const prev = events[i - 1];
    const curr = events[i];
    const diffMin = (curr.timestamp - prev.timestamp) / 60000;
    if (diffMin >= thresholdMinutes) {
      gaps.push({
        afterEvent: prev,
        beforeEvent: curr,
        minutes: Math.round(diffMin),
        start: prev.timestamp,
        end: curr.timestamp,
      });
    }
  }
  return gaps;
}

/** Flag events that exceed expected duration (when benchmark data available). */
export function flagLongRunningEvents(events, benchmarks, tasksById) {
  if (!benchmarks?.size) return events;
  return events.map((ev) => {
    if (ev.eventType !== PRODUCTION_EVENT_TYPES.TASK_COMPLETED) return ev;
    const task = tasksById?.get(ev.taskId) ?? ev;
    const key = getBenchmarkKey(task);
    const bench = benchmarks.get(key);
    const duration = ev.durationMinutes ?? task.netDurationMinutes ?? task.durationMinutes;
    const benchDur = bench?.netDurationMinutes ?? bench?.durationMinutes;
    if (!benchDur || !duration) return ev;
    if (duration > benchDur * 1.5) {
      return { ...ev, highlight: 'long-running' };
    }
    return ev;
  });
}

/** i18n keys for task edit field labels (used on Production Replay). */
export const TASK_EDIT_FIELD_I18N = {
  taskName: 'taskTable.taskName',
  customTaskName: 'production.customTaskName',
  productName: 'taskTable.product',
  sku: 'production.sku',
  lotNumber: 'production.lotNumber',
  quantityCompleted: 'taskTable.quantity',
  unitType: 'production.unitType',
  startTime: 'taskTable.startTime',
  endTime: 'taskTable.endTime',
  teamSize: 'taskTable.teamSize',
  labourCost: 'taskTable.labourCost',
  roomName: 'taskTable.room',
  notes: 'production.notes',
  downtimeMinutes: 'production.downtimeMinutes',
  downtimeReason: 'production.downtimeReason',
  tagNames: 'nav.taskTags',
};

const LABOUR_RATE_SOURCE_I18N = {
  individualRates: 'replayExtra.rateSources.individualRates',
  defaultTeamRate: 'replayExtra.rateSources.defaultTeamRate',
  partialRates: 'replayExtra.rateSources.partialRates',
  unavailable: 'replayExtra.rateSources.unavailable',
};

const EDIT_FIELD_DISPLAY_ORDER = [
  'taskName',
  'customTaskName',
  'productName',
  'sku',
  'lotNumber',
  'quantityCompleted',
  'unitType',
  'startTime',
  'endTime',
  'teamSize',
  'roomName',
  'labourCost',
  'labourRateSource',
  'downtimeMinutes',
  'downtimeReason',
  'tagNames',
  'notes',
];

export function humanizeFieldKey(key) {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

function isEmptyEditValue(val) {
  return val == null || val === '';
}

function normalizeEditComparable(val) {
  if (isEmptyEditValue(val)) return null;
  if (typeof val === 'object' && val !== null) {
    const d = parseFlexibleDate(val);
    if (d) return d.getTime();
  }
  if (typeof val === 'string') {
    const d = parseFlexibleDate(val);
    if (d) return d.getTime();
  }
  if (typeof val === 'number' && Number.isNaN(val)) return null;
  return val;
}

/** Normalize a task edit snapshot for comparison and replay display. */
export function normalizeTaskEditSnapshot(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [key, val] of Object.entries(raw)) {
    if (key === 'tagIds') continue;
    out[key] = normalizeTaskEditFieldValue(key, val);
  }
  return out;
}

function normalizeTaskEditFieldValue(key, val) {
  if (isEmptyEditValue(val)) return null;
  if (key === 'startTime' || key === 'endTime') {
    const d = parseFlexibleDate(val);
    return d ? d.toISOString() : null;
  }
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (key === 'teamSize' || key === 'quantityCompleted' || key === 'downtimeMinutes' || key === 'labourCost') {
      if (trimmed === '') return null;
      const num = Number(trimmed);
      return Number.isNaN(num) ? trimmed : num;
    }
    return trimmed || null;
  }
  if (key === 'tagNames' && Array.isArray(val)) {
    return val.length ? val : null;
  }
  return val ?? null;
}

/** Fields that meaningfully differ between normalized before/after snapshots. */
export function getTaskEditChangedFields(before, after) {
  const keys = sortEditFieldKeys(
    Object.keys(before || {}).filter(
      (key) => Object.prototype.hasOwnProperty.call(after || {}, key),
    ),
  );
  return keys.filter((key) => !editValuesEqual(before[key], after[key]));
}

function sortEditFieldKeys(keys) {
  const rank = new Map(EDIT_FIELD_DISPLAY_ORDER.map((key, index) => [key, index]));
  return [...keys].sort((a, b) => {
    const aRank = rank.has(a) ? rank.get(a) : Number.MAX_SAFE_INTEGER;
    const bRank = rank.has(b) ? rank.get(b) : Number.MAX_SAFE_INTEGER;
    if (aRank !== bRank) return aRank - bRank;
    return a.localeCompare(b);
  });
}

function editValuesEqual(a, b) {
  const left = normalizeEditComparable(a);
  const right = normalizeEditComparable(b);
  if (left === right) return true;
  if (typeof left === 'object' || typeof right === 'object') {
    return JSON.stringify(left) === JSON.stringify(right);
  }
  return false;
}

function collectEditFieldKeys(before, after, changedFields) {
  if (Array.isArray(changedFields) && changedFields.length) {
    return sortEditFieldKeys(changedFields.filter((key) => key !== 'tagIds'));
  }
  const keys = Object.keys(before || {}).filter(
    (key) => key !== 'tagIds' && Object.prototype.hasOwnProperty.call(after || {}, key),
  );
  return sortEditFieldKeys(keys);
}

function formatEditDate(val) {
  const d = parseFlexibleDate(val);
  return d ? formatDateTime(d) : '—';
}

function formatEditFieldDisplay(val, field, { currency = 'CAD' } = {}) {
  if (isEmptyEditValue(val)) return '—';
  if (field === 'startTime' || field === 'endTime') {
    return formatEditDate(val);
  }
  if (field === 'labourCost' && typeof val === 'number') {
    return formatCurrency(val, currency);
  }
  if (field === 'labourRateSource') {
    const i18nKey = LABOUR_RATE_SOURCE_I18N[val];
    return i18nKey ? i18n.t(i18nKey) : humanizeFieldKey(String(val));
  }
  if (field === 'tagNames' && Array.isArray(val)) {
    return val.length ? val.join(', ') : '—';
  }
  if (typeof val === 'object') {
    const d = parseFlexibleDate(val);
    if (d) return formatDateTime(d);
    if (Array.isArray(val)) return val.length ? val.join(', ') : '—';
    return '—';
  }
  return String(val);
}

export function parseTaskEditChanges(
  event,
  { labelForField = humanizeFieldKey, currency = 'CAD' } = {},
) {
  if (!event?.previousValue || !event?.newValue) return [];
  try {
    const before = JSON.parse(event.previousValue);
    const after = JSON.parse(event.newValue);
    if (typeof before !== 'object' || before === null || typeof after !== 'object' || after === null) {
      return [];
    }

    const keys = sortEditFieldKeys(
      collectEditFieldKeys(before, after, event.metadata?.changedFields),
    );

    return keys
      .filter((key) => !editValuesEqual(before[key], after[key]))
      .map((key) => ({
        field: key,
        label: labelForField(key),
        before: formatEditFieldDisplay(before[key], key, { currency }),
        after: formatEditFieldDisplay(after[key], key, { currency }),
      }));
  } catch {
    return [];
  }
}

export function formatEditSummary(event, options) {
  const changes = parseTaskEditChanges(event, options);
  if (!changes.length) return null;
  return changes.map((change) => `${change.label}: ${change.before} → ${change.after}`).join('; ');
}

export function computeHighCostThreshold(tasks) {
  const costs = tasks
    .map((t) => t.labourCost)
    .filter((c) => c != null && c > 0);
  return percentile(costs, 75);
}

export function buildReplayCSV(events, currency = 'CAD') {
  const headers = [
    i18n.t('export.replayCsv.time'),
    i18n.t('export.replayCsv.eventType'),
    i18n.t('export.replayCsv.task'),
    i18n.t('export.replayCsv.product'),
    i18n.t('export.replayCsv.tags'),
    i18n.t('export.replayCsv.workers'),
    i18n.t('export.replayCsv.room'),
    i18n.t('export.replayCsv.durationMin'),
    i18n.t('export.replayCsv.notes'),
    i18n.t('export.replayCsv.reason'),
  ];
  const rows = events.map((ev) => [
    ev.timestamp ? format(ev.timestamp, 'yyyy-MM-dd HH:mm:ss') : '',
    EVENT_TYPE_I18N[ev.eventType] ? i18n.t(EVENT_TYPE_I18N[ev.eventType]) : ev.eventType,
    ev.taskName || '',
    ev.productName || '',
    ev.tagNames?.length ? ev.tagNames.join('; ') : '',
    workerLabel(ev) || i18n.t('replayExtra.unassigned'),
    ev.roomName || '',
    ev.durationMinutes ?? '',
    ev.notes || '',
    ev.reason || '',
  ]);
  return { headers, rows, currency };
}

export function formatSummaryMetric(value, type, currency) {
  switch (type) {
    case 'duration':
      return value != null ? formatDuration(value) : '—';
    case 'cost':
      return value != null ? formatCurrency(value, currency) : '—';
    case 'costPerUnit':
      return value != null ? formatCurrency(value, currency) : '—';
    default:
      return value ?? '—';
  }
}

export const EVENT_TYPE_I18N = {
  [PRODUCTION_EVENT_TYPES.TASK_STARTED]: 'replayExtra.events.taskStarted',
  [PRODUCTION_EVENT_TYPES.TASK_PAUSED]: 'replayExtra.events.taskPaused',
  [PRODUCTION_EVENT_TYPES.TASK_RESUMED]: 'replayExtra.events.taskResumed',
  [PRODUCTION_EVENT_TYPES.TASK_COMPLETED]: 'replayExtra.events.taskCompleted',
  [PRODUCTION_EVENT_TYPES.TASK_CANCELLED]: 'replayExtra.events.taskCancelled',
  [PRODUCTION_EVENT_TYPES.WORKER_ADDED]: 'replayExtra.events.workerAdded',
  [PRODUCTION_EVENT_TYPES.WORKER_REMOVED]: 'replayExtra.events.workerRemoved',
  [PRODUCTION_EVENT_TYPES.HANDOFF]: 'replayExtra.events.handoff',
  [PRODUCTION_EVENT_TYPES.ROOM_CHANGED]: 'replayExtra.events.roomChanged',
  [PRODUCTION_EVENT_TYPES.MATERIAL_DELAY_LOGGED]: 'replayExtra.events.materialDelay',
  [PRODUCTION_EVENT_TYPES.NOTE_ADDED]: 'replayExtra.events.noteAdded',
  [PRODUCTION_EVENT_TYPES.QUANTITY_UPDATED]: 'replayExtra.events.quantityUpdated',
  [PRODUCTION_EVENT_TYPES.TASK_EDITED]: 'replayExtra.events.taskEdited',
};

/** @deprecated Use EVENT_TYPE_I18N with t() — kept for any external imports */
export const EVENT_TYPE_LABELS = Object.fromEntries(
  Object.entries(EVENT_TYPE_I18N).map(([type, key]) => [type, i18n.t(key)]),
);

export function eventGroupForType(eventType) {
  for (const [group, types] of Object.entries(EVENT_TYPE_GROUPS)) {
    if (types.has(eventType)) return group;
  }
  return 'other';
}
